import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { processB2CLogin } from "../utils/jwtParser";

/**
 * Custom hook for B2C email extraction and management
 * Matches the email handling logic from the APIM project
 */
export const useB2CEmail = () => {
  const { accounts, instance } = useMsal();
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const extractAndStoreEmail = async () => {
      try {
        // If user is authenticated, process the ID token
        if (accounts && accounts.length > 0) {
          const account = accounts[0];
          
          try {
            const silentRequest = {
              scopes: ["openid", "profile", "email"],
              account: account,
            };

            const response = await instance.acquireTokenSilent(silentRequest);
            
            if (response.idToken) {
              // Process the ID token to extract email
              const extractedEmail = processB2CLogin(response.idToken);
              setEmail(extractedEmail);
            }
          } catch (silentError) {
            console.warn("Silent token acquisition failed, using existing account data:", silentError);
            
            // Fallback: try to get email from account data directly
            const fallbackEmail = (account as any).idTokenClaims?.emailAddress || 
                                (account as any).idTokenClaims?.email || 
                                (account as any).idTokenClaims?.emails?.[0] ||
                                account.username;
            
            if (fallbackEmail) {
              setEmail(fallbackEmail);
            }
          }
        }
      } catch (error) {
        console.error("Failed to extract B2C email:", error);
      }
    };

    extractAndStoreEmail();
  }, [accounts, instance]);

  // Clear email when user logs out
  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      setEmail(null);
    }
  }, [accounts]);

  return {
    email,
    refreshEmail: () => {
      setEmail(null);
      // This will trigger re-extraction on next render
    }
  };
};

/**
 * Utility function to get user display name
 * Combines given_name and family_name or falls back to name
 */
export const useB2CUserName = () => {
  const { accounts } = useMsal();

  if (!accounts || accounts.length === 0) {
    return null;
  }

  const account = accounts[0];
  const claims = (account as any).idTokenClaims;

  // Try to construct name from given_name and family_name
  if (claims?.given_name && claims?.family_name) {
    return `${claims.given_name} ${claims.family_name}`;
  }

  // Fallback to name claim or account name
  return claims?.name || account.name || 'User';
};